'use client';

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { TFunction } from 'i18next';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { css } from '@bts-web/utils-style-engine';
import { IvyCheckout } from '@getivy/react-sdk';
import './TransferStatusScreen.css';
import { NavigationBar } from '../../../common/components/NavigationBar/NavigationBar';
import { NavigationBackButton } from '../../../navigation/NavigationBackButton/NavigationBackButton.use-client';
import { CancelTradeWithConfirmButton } from '../../Reusable/CancelTradeConfirmationModal/CancelTradeWithConfirmButton';
import { getTradeTranslations } from '../../utils/getTradeTranslations';
import { getAssetTradeUrl } from '../../../route-builders';
import { useTransferStatusPolling } from './useTransferStatusPolling';
import { datadogErrorHelper } from '../../../common/utils/datadogErrorHelper';
import {
  DatadogErrorLevels,
  NEXT_ROUTES,
} from '@bts-web/utils-next-infrastructure';
import { useAppNotification } from '../../../notifications/NotificationContextProvider';
import { ErrorMessage } from '../../../common/components/ErrorMessage/ErrorMessage';
import { getTransferStatusDescription } from '../../utils/getTransferStatusDescription';
import { getTransferErrorDescription } from '../../utils/getTransferErrorDescription';
import { TransferLoading } from './TransferStatusScreenLoading';
import { ManualReviewScreen } from './ManualReviewScreen';
import {
  hasDismissedOpenBankingInfoScreen,
  OpenBankingInfoScreen,
} from '../Deposit/OpenBankingInfoScreen';

interface TransferStatusScreenProps {
  assetId: string;
  offerId: string;
  transferLink?: string;
  initialErrors?: string[];
  amount?: string;
}

const StableIvyCheckout = memo(IvyCheckout);

export const TransferStatusScreen = ({
  assetId,
  offerId,
  transferLink,
  initialErrors,
  amount,
}: TransferStatusScreenProps) => {
  const router = useRouter();

  const { t } = useClientTranslation();

  const translations = getTradeTranslations(t as TFunction);

  const { setAppNotification } = useAppNotification();

  const isResume = transferLink === undefined;

  const [showOpenBankingScreen, setShowOpenBankingScreen] = useState(
    () => transferLink !== undefined && !hasDismissedOpenBankingInfoScreen(),
  );

  const [ivyVisible, setIvyVisible] = useState(
    () => transferLink !== undefined && hasDismissedOpenBankingInfoScreen(),
  );

  // With a transferLink we show Ivy first and only start polling once Ivy
  // succeeds. On resume (no transferLink) we poll immediately if we have an offer.
  const [startPolling, setStartPolling] = useState(
    transferLink === undefined && Boolean(offerId),
  );

  const { status, errors, transferStatus } = useTransferStatusPolling({
    id: offerId ?? '',
    enabled: startPolling,
    initialErrors: initialErrors?.filter(
      (error) => error != 'fiat.transfer.in.progress',
    ),
  });

  const errorDescription = getTransferErrorDescription(errors, translations);

  useEffect(() => {
    if (status === 'terminal') {
      if (transferStatus === 'CREDITED' || transferStatus === 'FINISHED') {
        setAppNotification({
          visual: 'success',
          position: 'topRight',
          title: translations.fiatTransferSuccessNotificationTitle,
        });

        router.replace(
          getAssetTradeUrl(assetId, {
            type: 'acceptOffer',
            variant: 'buy',
            offerId,
          }),
        );
      }
    } else if (status === 'timeout') {
      setAppNotification({
        visual: 'info',
        title: translations.fiatTransferRedirectNotificationTitle,
        subtitle: translations.fiatTransferRedirectNotificationSubtitle,
      });

      router.push(NEXT_ROUTES.PORTFOLIO);
    } else if (status === 'error' && !errorDescription) {
      // Known business errors (offer expired, funds consumed) are expected and
      // shown to the user directly; only log unrecognized errors as critical.
      datadogErrorHelper({
        errorMessage: errors ? JSON.stringify(errors) : 'Internal server error',
        errorSeverity: DatadogErrorLevels.CRITICAL,
      });

      router.push(NEXT_ROUTES.ERROR_GENERIC);
    }
  }, [status, assetId, offerId, router, transferStatus, errorDescription]);

  const retryButtonCallback = () => {
    router.replace(
      getAssetTradeUrl(assetId, {
        type: 'buy',
      }),
    );
  };

  const handleIvySuccess = useCallback(() => {
    setIvyVisible(false);

    setStartPolling(true);
  }, []);

  const handleIvyCancel = useCallback(() => {
    setStartPolling(false);

    setIvyVisible(false);

    setShowOpenBankingScreen(false);

    router.push(getAssetTradeUrl(assetId, { type: 'buy' }));
  }, [assetId, router]);

  const handleProceedFromNotification = useCallback(() => {
    setShowOpenBankingScreen(false);

    setIvyVisible(true);
  }, []);

  const isTerminalFailure =
    status === 'terminal' &&
    (transferStatus === 'REJECTED' ||
      transferStatus === 'REFUNDED' ||
      transferStatus === 'CANCELLED');

  const isInManualReview =
    status === 'terminal' && transferStatus === 'MANUAL_REVIEW';

  const ivyDisplayOptions = useMemo(() => ({ type: 'embedded' as const }), []);

  if (isTerminalFailure || status === 'error') {
    return (
      <div
        className={css({
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'medium',
          padding: 'large',
        })}
      >
        <ErrorMessage
          title={translations.fiatTransferFailedTitle}
          description={
            errorDescription ??
            getTransferStatusDescription(transferStatus, translations)
          }
          withRetryButton
          withPortfolioButton
          retryButtonCallback={retryButtonCallback}
        />
      </div>
    );
  }

  if (isInManualReview) {
    return (
      <ManualReviewScreen
        title={translations.depositManualReviewTitle}
        description={translations.depositManualReviewDescription}
      />
    );
  }

  if (showOpenBankingScreen) {
    return (
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          width: '100%',
        })}
      >
        <OpenBankingInfoScreen onProceed={handleProceedFromNotification} />
      </div>
    );
  }

  if (ivyVisible) {
    return (
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          width: '100%',
        })}
      >
        <StableIvyCheckout
          checkoutUrl={transferLink ?? ''}
          displayOptions={ivyDisplayOptions}
          onSuccess={handleIvySuccess}
          onCancel={handleIvyCancel}
        />
      </div>
    );
  }

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
      })}
    >
      <NavigationBar
        title={translations.fiatTransferRedirectTitle}
        NavigationLeftSlot={<NavigationBackButton />}
        NavigationRightSlot={
          <CancelTradeWithConfirmButton translations={translations} />
        }
      />
      <div
        className={css({
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'medium',
          padding: 'large',
        })}
      >
        <TransferLoading
          translations={translations}
          isResume={isResume}
          t={t}
          amount={amount}
        />
      </div>
    </div>
  );
};
